
import { computed, defineComponent, reactive, ref } from "vue";
import {
  ApplicationType,
  NotificationEmailType,
  useGetApplicationsQuery,
  useUpdateNotificationOptionsMutation,
  SettingItemType,
} from "@/graphql/types";
import ApplicationsPicker from "../pickers/ApplicationsPicker.vue";
import { VDPopupListboxClass } from "../pickers/VDPopupListbox";
import Error from "@/components/Error.vue";
import { useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import useError from "@/composables/useError";
import useToastService from "@/composables/useToastService";

gql`
  mutation UpdateNotificationOptions(
    $emailId: ID!
    $input: CreateOrUpdateNotificationOptionsInputType!
    $settingItemType: SettingItemType!
    $activate: Boolean
    $applicationName: String
  ) {
    updateNotificationOptions(
      emailId: $emailId
      input: $input
      settingItemType: $settingItemType
      activate: $activate
      applicationName: $applicationName
    )
  }
`;

export default defineComponent({
  components: { ApplicationsPicker, Error },
  emits: ["reloadEmailsNotification"],

  setup(props, { emit }) {
    const toastService = useToastService();

    const applicationsPicker = ref<VDPopupListboxClass>();

    const isVisible = ref(false);

    const notificationEmail = reactive<any>({
      emailId: "",
      emailAddress: "",
      notificationOptions: {
        onApplicationChanges: false,
        onUserChanges: false,
        onRoleChanges: false,
        onRoleProfileChanges: false,
        onOrganizationChanges: false,
        onSystemChanges: false,
        onUserOnboardingPerApplication: [],
      },
    });

    const settingItemTypeEnum = SettingItemType;

    const notificationMessage = ref<string>("");

    function reset() {
      isVisible.value = false;
      Object.assign(notificationEmail, {});
      emit("reloadEmailsNotification");
    }

    // Data
    const { result, loading } = useGetApplicationsQuery();

    function applicationInit() {
      return (
        useResult(result, null, (data) => data?.applications)
          .value?.slice()
          ?.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? "")) ?? []
      );
    }

    function onAddedApplication(selectedApp: ApplicationType) {
      notificationEmail.notificationOptions.onUserOnboardingPerApplication.push(
        selectedApp.applicationId
      );
      var applicationName = selectedApp.name ? selectedApp.name : "";
      onUpdateEmailNotificationOptions(
        settingItemTypeEnum.AddApplicationChangesNotificationList,
        false,
        applicationName
      );
      notificationMessage.value = `'${applicationName}' er tilføjet til listen for onboarding-notifikationer`;
    }

    function onRemoveApplication(selectedApp: ApplicationType) {
      notificationEmail.notificationOptions.onUserOnboardingPerApplication =
        notificationEmail.notificationOptions.onUserOnboardingPerApplication.filter(
          (id: string) => id !== selectedApp.applicationId
        );
      var applicationName = selectedApp.name ? selectedApp.name : "";
      onUpdateEmailNotificationOptions(
        settingItemTypeEnum.RemoveApplicationChangesNotificationList,
        false,
        applicationName
      );
      notificationMessage.value = `'${applicationName}' er fjernet fra listen for onboarding-notifikationer`;
    }

    const updateUpdateNotificationOptionsMutation =
      useUpdateNotificationOptionsMutation({});
    updateUpdateNotificationOptionsMutation.onDone(() => {
      toastService.show({
        severity: "success",
        title: "Indstillinger opdateret",
        message: notificationMessage.value,
      });
      notificationMessage.value = "";
    });
    updateUpdateNotificationOptionsMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );

    const onUpdateEmailNotificationOptions = async (
      settingItemType: SettingItemType,
      activate?: boolean,
      applicationName?: string
    ) => {
      try {
        // Set notification message based on setting item type
        if (
          settingItemType !==
            settingItemTypeEnum.AddApplicationChangesNotificationList &&
          settingItemType !==
            settingItemTypeEnum.RemoveApplicationChangesNotificationList
        ) {
          // Handled in onAddedApplication and onRemoveApplication
          var activateMessage = activate ? "aktiveret" : "deaktiveret";
          switch (settingItemType) {
            case settingItemTypeEnum.UpdateApplicationChangesNotificationList:
              notificationMessage.value = `'Send e-mail ved applikationsændringer' er blevet ${activateMessage}`;
              break;
            case settingItemTypeEnum.UpdateUserChangesNotificationList:
              notificationMessage.value = `'Send e-mail ved brugerændringer' er blevet ${activateMessage}`;
              break;
            case settingItemTypeEnum.UpdateRoleChangesNotificationList:
              notificationMessage.value = `'Send e-mail ved rolleændringer' er blevet ${activateMessage}`;
              break;
            case settingItemTypeEnum.UpdateRoleProfileChangesNotificationList:
              notificationMessage.value = `'Send e-mail ved rolleprofilændringer' er blevet ${activateMessage}`;
              break;
            case settingItemTypeEnum.UpdateOrganizationChangesNotificationList:
              notificationMessage.value = `'Send e-mail ved organisationsændringer' er blevet ${activateMessage}`;
              break;
            case settingItemTypeEnum.UpdateSystemChangesNotificationList:
              notificationMessage.value = `'Send e-mail ved systemændringer' er blevet ${activateMessage}`;
              break;
            default:
              notificationMessage.value = "Indstillinger opdateret";
              break;
          }
        }

        updateUpdateNotificationOptionsMutation.mutate({
          emailId: notificationEmail.emailId,
          settingItemType: settingItemType,
          applicationName: applicationName!,
          activate: activate!,
          input: {
            onApplicationChanges:
              notificationEmail.notificationOptions.onApplicationChanges,
            onUserChanges: notificationEmail.notificationOptions.onUserChanges,
            onRoleChanges: notificationEmail.notificationOptions.onRoleChanges,
            onRoleProfileChanges:
              notificationEmail.notificationOptions.onRoleProfileChanges,
            onOrganizationChanges:
              notificationEmail.notificationOptions.onOrganizationChanges,
            onSystemChanges:
              notificationEmail.notificationOptions.onSystemChanges,
            onUserOnboardingPerApplication:
              notificationEmail.notificationOptions
                .onUserOnboardingPerApplication,
          },
        });
      } catch (error) {
        console.error(error);
      }
    };

    return {
      isVisible,
      notificationEmail,
      show: (value: NotificationEmailType | null) => {
        Object.assign(notificationEmail, value);
        notificationEmail.notificationOptions = JSON.parse(
          JSON.stringify(value?.notificationOptions ?? {})
        );
        isVisible.value = true;
      },
      hide: () => reset(),
      applicationsPicker,
      isApplicationOptionsLoading: computed(() => loading.value),
      applicationOptions: computed(() => applicationInit()),
      selectedApplications: computed(() =>
        applicationInit().filter((app) =>
          notificationEmail.notificationOptions.onUserOnboardingPerApplication?.includes(
            app?.applicationId ?? ""
          )
        )
      ),
      onRemoveApplication,
      onAddedApplication,
      updateUpdateNotificationOptionsMutation,
      onUpdateEmailNotificationOptions,
      settingItemTypeEnum,
    };
  },
});
